import { useLazyQuery, useMutation } from '@apollo/client'
import {
  Box,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField as MuiTextField,
  Typography,
} from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import SearchIcon from '@mui/icons-material/Search'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Button } from 'ui/baseComponents'
import {
  GET_PATIENTS_BY_TAG,
  GET_TAG_STATS,
  LIST_TAGS,
  useTagStats,
} from '../../graphql/tagQueries'
import {
  TEST_TAG_CRITERIA,
  EXECUTE_TAG_CRITERIA,
} from '../../graphql/tagMutations'
import { formatDateString } from 'utilities/time'

const PATIENTS_PAGE_SIZE = 50

const ActionButtons = ({
  hasCriteria,
  testing,
  executing,
  loading,
  onTest,
  onExecute,
  onViewPatients,
}) => (
  <Box sx={{ display: 'flex', gap: 1 }}>
    {hasCriteria && (
      <>
        <Button
          type="secondary"
          size="small"
          onClick={onTest}
          disabled={testing || executing || loading}
        >
          {testing ? 'Testing...' : 'Test Criteria'}
        </Button>
        <Button
          type="secondary"
          size="small"
          onClick={onExecute}
          disabled={testing || executing || loading}
        >
          {executing ? 'Executing...' : 'Execute Tag'}
        </Button>
      </>
    )}
    <Button
      type="secondary"
      size="small"
      onClick={onViewPatients}
      disabled={testing || executing || loading}
    >
      {loading ? 'Loading...' : 'View Patients'}
    </Button>
  </Box>
)

const StatusSummary = ({ totalCount, mode }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
    {totalCount !== null && (
      <Chip
        label={`${totalCount} patient${totalCount !== 1 ? 's' : ''}`}
        size="small"
        color={mode === 'preview' ? 'warning' : 'default'}
      />
    )}
    {mode === 'preview' && (
      <Typography variant="body2" color="warning.main" fontWeight={500}>
        Preview — execute to apply
      </Typography>
    )}
    {mode === 'tagged' && totalCount !== null && (
      <Typography variant="body2" color="success.main" fontWeight={500}>
        Tagged
      </Typography>
    )}
  </Box>
)

const TaggedPatientsPanel = ({ tag }) => {
  const [patients, setPatients] = useState(null)
  const [patientsCursor, setPatientsCursor] = useState(null)
  const [loadingPatients, setLoadingPatients] = useState(false)
  const [error, setError] = useState(null)
  const [search, setSearch] = useState('')
  const [copied, setCopied] = useState(false)
  const [panelVisible, setPanelVisible] = useState(true)
  const [footerLeft, setFooterLeft] = useState(0)

  const sentinelRef = useRef(null)
  const scrollParentRef = useRef(null)

  // Preview state: result of "Test Criteria"
  const [previewPatientIds, setPreviewPatientIds] = useState(null)
  const [previewCount, setPreviewCount] = useState(null)

  // Actual tagged state: result of "View Patients" or "Execute"
  const [mode, setMode] = useState(null) // 'preview' | 'tagged' | null

  const { data: statsData, refetch: refetchStats } = useTagStats(tag.id)
  const stats = statsData?.tagStats ?? null

  const [fetchPatients] = useLazyQuery(GET_PATIENTS_BY_TAG, {
    fetchPolicy: 'network-only',
  })

  const [testCriteria, { loading: testing }] = useMutation(TEST_TAG_CRITERIA)
  const [executeCriteria, { loading: executing }] = useMutation(
    EXECUTE_TAG_CRITERIA,
    {
      refetchQueries: [
        { query: LIST_TAGS },
        { query: GET_TAG_STATS, variables: { tagId: tag.id } },
      ],
    },
  )

  useEffect(() => {
    const el = sentinelRef.current
    if (!el) return

    // Find the closest scrollable ancestor
    let scrollParent = el.parentElement
    while (scrollParent) {
      const { overflow, overflowY } = getComputedStyle(scrollParent)
      if (/auto|scroll/.test(overflow + overflowY)) break
      scrollParent = scrollParent.parentElement
    }
    if (!scrollParent) return

    scrollParentRef.current = scrollParent

    const checkVisibility = () => {
      const containerRect = scrollParent.getBoundingClientRect()
      const elRect = el.getBoundingClientRect()
      const isVisible =
        elRect.top < containerRect.bottom && elRect.bottom > containerRect.top
      setPanelVisible(isVisible)
      setFooterLeft(containerRect.left)
    }

    checkVisibility()
    scrollParent.addEventListener('scroll', checkVisibility, { passive: true })
    window.addEventListener('resize', checkVisibility, { passive: true })

    const resizeObserver = new ResizeObserver(checkVisibility)
    resizeObserver.observe(scrollParent)

    return () => {
      scrollParent.removeEventListener('scroll', checkVisibility)
      window.removeEventListener('resize', checkVisibility)
      resizeObserver.disconnect()
    }
  }, [])

  const handleTest = useCallback(async () => {
    setError(null)
    setPatients(null)
    setPatientsCursor(null)
    setPreviewPatientIds(null)
    setPreviewCount(null)
    try {
      const input = {}
      if (tag.include?.length) input.include = tag.include
      if (tag.exclude?.length) input.exclude = tag.exclude
      const result = await testCriteria({ variables: { input } })
      const data = result.data.testTagCriteria
      setPreviewPatientIds(data.patientIds || [])
      setPreviewCount(data.patientCount)
      setMode('preview')
    } catch (err) {
      setError('Failed to test criteria. Please try again.')
    }
  }, [tag, testCriteria])

  const handleExecute = useCallback(async () => {
    setError(null)
    setPatients(null)
    setPatientsCursor(null)
    setPreviewPatientIds(null)
    setPreviewCount(null)
    try {
      const result = await executeCriteria({
        variables: { input: { tagId: tag.id } },
      })
      const data = result.data.executeTagCriteria
      setPreviewPatientIds(data.patientIds || [])
      setPreviewCount(data.patientCount)
      setMode('tagged')
      refetchStats()
    } catch (err) {
      setError('Failed to execute criteria. Please try again.')
    }
  }, [tag.id, executeCriteria, refetchStats])

  const handleViewPatients = useCallback(async () => {
    setError(null)
    setPreviewPatientIds(null)
    setPreviewCount(null)
    setPatients(null)
    setPatientsCursor(null)
    setLoadingPatients(true)
    try {
      const { data } = await fetchPatients({
        variables: { tagId: tag.id, cursor: null, limit: PATIENTS_PAGE_SIZE },
      })
      const pageData = data?.patientListByTag || {}
      setPatients(pageData.patients || [])
      setPatientsCursor(pageData.pageInfo?.endCursor || null)
      setMode('tagged')
    } catch (err) {
      setError('Failed to load patients. Please try again.')
    } finally {
      setLoadingPatients(false)
    }
  }, [tag.id, fetchPatients])

  const handleLoadMorePatients = useCallback(async () => {
    if (!patientsCursor) return
    setLoadingPatients(true)
    try {
      const { data } = await fetchPatients({
        variables: {
          tagId: tag.id,
          cursor: patientsCursor,
          limit: PATIENTS_PAGE_SIZE,
        },
      })
      const pageData = data?.patientListByTag || {}
      setPatients((prev) => [...(prev || []), ...(pageData.patients || [])])
      setPatientsCursor(pageData.pageInfo?.endCursor || null)
    } catch (err) {
      setError('Failed to load more patients. Please try again.')
    } finally {
      setLoadingPatients(false)
    }
  }, [tag.id, patientsCursor, fetchPatients])

  const handleCopyAll = useCallback(() => {
    let ids
    if (patients) {
      ids = patients.map((p) => p.id)
    } else if (previewPatientIds) {
      ids = previewPatientIds
    } else {
      return
    }
    navigator.clipboard.writeText(ids.join('\n'))
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }, [patients, previewPatientIds])

  const displayList = useMemo(() => {
    if (patients) {
      return patients.map((p) => ({ id: p.id, codeName: p.codeName }))
    }
    if (previewPatientIds) {
      return previewPatientIds.map((id) => ({ id, codeName: null }))
    }
    return null
  }, [patients, previewPatientIds])

  const filteredList = useMemo(() => {
    if (!displayList) return null
    if (!search.trim()) return displayList
    const q = search.toLowerCase()
    return displayList.filter(
      (p) =>
        p.id.toLowerCase().includes(q) ||
        (p.codeName || '').toLowerCase().includes(q),
    )
  }, [displayList, search])

  const totalCount =
    previewCount ??
    (mode === 'tagged' && patients ? stats?.patientCount ?? null : null)

  const hasCriteria =
    (tag.include && tag.include.length > 0) ||
    (tag.exclude && tag.exclude.length > 0)

  const showFooter = !panelVisible

  const actionProps = {
    hasCriteria,
    testing,
    executing,
    loading: loadingPatients,
    onTest: handleTest,
    onExecute: handleExecute,
    onViewPatients: handleViewPatients,
  }

  return (
    <>
      <Box ref={sentinelRef}>
        {/* Summary bar */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6">Patients</Typography>
            {stats?.patientCount != null && mode === null ? (
              <Chip
                label={`${stats.patientCount} patient${
                  stats.patientCount !== 1 ? 's' : ''
                } tagged`}
                size="small"
              />
            ) : (
              <StatusSummary totalCount={totalCount} mode={mode} />
            )}
            {stats?.lastEvaluatedAt != null && (
              <Typography variant="body2" color="text.secondary">
                Last evaluated:{' '}
                {formatDateString({
                  dateString: new Date(
                    stats.lastEvaluatedAt * 1000,
                  ).toISOString(),
                  format: 'MMM DD, YYYY h:mm A',
                })}
              </Typography>
            )}
          </Box>
          <ActionButtons {...actionProps} />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {mode === 'tagged' &&
          !loadingPatients &&
          !patients &&
          previewPatientIds && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Tag executed successfully. {totalCount} patient(s) tagged.
            </Alert>
          )}

        {/* Patient table */}
        {filteredList && filteredList.length > 0 && (
          <Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                mb: 1.5,
              }}
            >
              <MuiTextField
                placeholder="Search by ID or code name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                size="small"
                sx={{ flex: 1, maxWidth: 400 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <IconButton
                  size="small"
                  onClick={handleCopyAll}
                  title="Copy all patient IDs"
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
                {copied && (
                  <Typography variant="caption" color="success.main">
                    Copied!
                  </Typography>
                )}
              </Box>
              {search && (
                <Typography variant="body2" color="text.secondary">
                  {filteredList.length} match
                  {filteredList.length !== 1 ? 'es' : ''}
                </Typography>
              )}
            </Box>

            <TableContainer sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 60 }}>#</TableCell>
                    <TableCell>Patient ID</TableCell>
                    {patients && <TableCell>Code Name</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredList.map((p, i) => (
                    <TableRow key={p.id} hover>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {i + 1}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: 'monospace', fontSize: 12 }}
                        >
                          {p.id}
                        </Typography>
                      </TableCell>
                      {patients && (
                        <TableCell>
                          <Typography variant="body2">
                            {p.codeName || '-'}
                          </Typography>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {patientsCursor && !search && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5 }}>
                <Button
                  type="secondary"
                  size="small"
                  onClick={handleLoadMorePatients}
                  disabled={loadingPatients}
                >
                  {loadingPatients ? 'Loading...' : 'Show more'}
                </Button>
              </Box>
            )}
          </Box>
        )}

        {filteredList && filteredList.length === 0 && search && (
          <Typography variant="body2" color="text.secondary">
            No patients match &ldquo;{search}&rdquo;
          </Typography>
        )}

        {filteredList && filteredList.length === 0 && !search && (
          <Typography variant="body2" color="text.secondary">
            No patients found.
          </Typography>
        )}
      </Box>

      {/* Sticky footer when panel is scrolled offscreen */}
      {showFooter && (
        <Paper
          elevation={0}
          sx={{
            position: 'fixed',
            bottom: 0,
            left: footerLeft,
            right: 0,
            zIndex: 1200,
            borderRadius: 0,
            borderTop: 1,
            borderColor: 'divider',
            boxShadow: '0 -2px 8px rgba(0,0,0,0.1)',
            pl: 3,
            pr: 10,
            py: 1.5,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              maxWidth: 1200,
              mx: 'auto',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="subtitle2">Patients</Typography>
              {stats?.patientCount != null && mode === null ? (
                <Chip
                  label={`${stats.patientCount} patient${
                    stats.patientCount !== 1 ? 's' : ''
                  } tagged`}
                  size="small"
                />
              ) : (
                <StatusSummary totalCount={totalCount} mode={mode} />
              )}
            </Box>
            <ActionButtons {...actionProps} />
          </Box>
        </Paper>
      )}
    </>
  )
}

export default TaggedPatientsPanel
